import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Skeleton, Tab, Tabs } from '@mui/material';
import { parseDashboardOverview } from '../dashboard/dashboardHelpers';
import { getOverviewData, clearDashboardError } from '../../store/slices/dashboardSlice';

const ALL_TAB_VALUE = 'all';

/**
 * Role-based dashboard statuses as tabs on the leads list.
 * Active tab filters by status_id (same query as dashboard deep-link).
 */
const LeadStatusQuickFilters = ({
  selectedStatusId = '',
  onStatusFilter,
}) => {
  const dispatch = useDispatch();
  const overview = useSelector((state) => state.dashboard.overview);
  const loading = useSelector((state) => state.dashboard.loading);

  useEffect(() => {
    dispatch(getOverviewData());
    return () => {
      dispatch(clearDashboardError());
    };
  }, [dispatch]);

  const { sections } = useMemo(() => parseDashboardOverview(overview), [overview]);

  const statusTabs = useMemo(() => {
    const seen = new Set();
    const tabs = [];
    sections.forEach((section) => {
      (section.items || []).forEach((item) => {
        const statusId = String(item?.statusId || '').trim();
        const key = statusId || String(item?.id || item?.label || '');
        if (!key || seen.has(key)) return;
        seen.add(key);
        tabs.push({
          value: statusId || key,
          statusId,
          statusName: String(item?.statusName || item?.label || '').trim(),
          label: item?.label || item?.statusName || 'Status',
          count: item?.count,
        });
      });
    });
    return tabs;
  }, [sections]);

  const activeTab = useMemo(() => {
    if (!selectedStatusId) return ALL_TAB_VALUE;
    const match = statusTabs.find(
      (tab) => String(tab.statusId) === String(selectedStatusId)
    );
    return match ? match.value : ALL_TAB_VALUE;
  }, [selectedStatusId, statusTabs]);

  const handleTabChange = (_event, nextValue) => {
    if (typeof onStatusFilter !== 'function') return;
    if (nextValue === ALL_TAB_VALUE) {
      onStatusFilter('', '');
      return;
    }
    const tab = statusTabs.find((item) => String(item.value) === String(nextValue));
    if (!tab) return;
    onStatusFilter(tab.statusId, tab.statusName);
  };

  if (loading && !statusTabs.length) {
    return (
      <Box sx={{ mb: 2 }}>
        <Skeleton animation="wave" height={40} width="100%" />
      </Box>
    );
  }

  if (!statusTabs.length) return null;

  return (
    <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Filter leads by status"
      >
        <Tab
          value={ALL_TAB_VALUE}
          label="All"
          sx={{ textTransform: 'none', minHeight: 44 }}
        />
        {statusTabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={
              tab.count != null && tab.count !== ''
                ? `${tab.label} (${tab.count})`
                : tab.label
            }
            sx={{ textTransform: 'none', minHeight: 44 }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default LeadStatusQuickFilters;
